import { debounce } from "@util/debounce";
import { rememberKeyboardNavigation } from "@util/keyboard-navigation";
import { useMainMenu } from "@features/Layout/Features/Header/Scripts/main-navigation";
import Disturbance from "@features/Layout/Features/Header/Partials/Disturbance/Scripts/disturbance";
import ToolMenu from "@features/Layout/Features/Header/Partials/ToolMenu/Scripts/tool-menu";
import Menu from "@features/Layout/Features/Header/Partials/Menu/Scripts/menu";

enum Direction {
    Up,
    Down
}

const useHeader = (): void => {
    const element = document.querySelector('.header') as HTMLElement;
    let elementHeight: number = 0;
    let bodyRect: number = 0;
    let scrollDirection: Direction = Direction.Down;
    let scrollPosition: number = Math.ceil(window.scrollY);
    let headerHasFocus: boolean = false;
    let stationScrollPosition = 0;
    let lastStationScroll = 0;

    const shouldLockHeader = () =>
        document.body.classList.contains('header-locked') ||
        document.body.hasAttribute('data-lock-header');

    const setScrollDirection = (): void => {
        if ((document.body.getBoundingClientRect()).top > bodyRect) {
            scrollDirection = Direction.Up;
        } else {
            scrollDirection = Direction.Down;
        }

        bodyRect = (document.body.getBoundingClientRect()).top;
    };

    const checkIfHeaderHasFocus = (event: FocusEvent): void => {
        if (event.target instanceof HTMLElement) {
            const elementWithFocus = event.target as HTMLElement;

            if (element?.contains(elementWithFocus)) {
                headerHasFocus = true;
            } else {
                headerHasFocus = false;
            }
        }
    };

    const toggleVisibility = (): void => {
        if (shouldLockHeader()) {
            element?.classList.remove('header--hidden');
            scrollPosition = Math.ceil(window.scrollY);
            return;
        }

        if (document.body.classList.contains('audio-guide-stations-menu-open')) {
            if (headerHasFocus || document.body.classList.contains('main-navigation-is-open')) show();
            return;
        }

        if (Math.ceil(window.scrollY) > scrollPosition + elementHeight) {
            hide();
        }

        if (scrollDirection === Direction.Up || scrollDirection === Direction.Down && Math.ceil(window.scrollY) + window.innerHeight >= document.body.scrollHeight || document.body.classList.contains('main-navigation-is-open') || headerHasFocus) {
            show();
            scrollPosition = Math.ceil(window.scrollY);
        }
    };

    const hide = (): void => {
        if (!element?.classList.contains('header--hidden')) {
            element?.classList.add('header--hidden');
        }
    }

    const show = (): void => {
        if (element?.classList.contains('header--hidden')) {
            element?.classList.remove('header--hidden');
        }
    }

    const onStationScroll = (event: Event): void => {
        if (!document.body.classList.contains('audio-guide-stations-menu-open')) return;
        const { menu, initialize } = (event as CustomEvent<{ menu: HTMLElement; initialize: boolean }>).detail;
        const position = Math.ceil(menu.scrollTop);
        if (initialize) {
            stationScrollPosition = position;
            lastStationScroll = position;
            return;
        }
        if (shouldLockHeader() || headerHasFocus || document.body.classList.contains('main-navigation-is-open')) {
            show();
            stationScrollPosition = position;
        } else if (position < lastStationScroll || position + menu.clientHeight >= menu.scrollHeight) {
            show();
            stationScrollPosition = position;
        } else if (position > stationScrollPosition + elementHeight) {
            hide();
        }
        lastStationScroll = position;
    };

    const initialize = (): void => {
        if (!element) {
            return;
        }

        if (document.body.classList.contains('audio-guide-page')) {
            element.addEventListener('click', (event: MouseEvent) => {
                const link = event.target instanceof Element
                    ? event.target.closest<HTMLAnchorElement>('.header__skip-link') : null;
                if (!link || event.defaultPrevented || event.button !== 0 || event.ctrlKey ||
                    event.metaKey || event.shiftKey || event.altKey) return;

                // Native fragment navigation would replace the guide's hash route.
                const target = link.hash === '#main-content'
                    ? document.getElementById('main-content')
                    : link.hash === '#main-navigation'
                        ? element.querySelector<HTMLButtonElement>('.main-navigation__toggle-button') : null;
                if (!target) return;

                event.preventDefault();
                show();
                target.focus({ preventScroll: true });
                window.scrollTo({ top: 0, behavior: 'instant' });
            });
        }
        element.addEventListener('click', rememberKeyboardNavigation);
        window.addEventListener('audio-guide-stations-scroll', onStationScroll);
        elementHeight = element.offsetHeight;
        new Disturbance();
        new ToolMenu();
        useMainMenu(element);
        new Menu();

        window.addEventListener('scroll', debounce(() => {
            setScrollDirection();
            toggleVisibility();
        }, 100));

        window.addEventListener('focus', (event) => {
            checkIfHeaderHasFocus(event);
            toggleVisibility();
        }, true);
    };

    initialize();
}

if (document.readyState === 'loading') {
    // DOM is not ready yet - wait for it
    document.addEventListener('DOMContentLoaded', () => {
        useHeader();
    });
} else {
    // DOM is already ready - run code immediately
    useHeader();
}